/**
 * RefreshLocalCurrencyExchangeRateUseCase
 *
 * Fetches the Revolut exchange rate from USD to the user's local wallet
 * currency and returns just the numeric `rate` (or `null` on failure).
 * The caller is responsible for persisting the rate into Redux — the
 * slice work is a separate dispatch and lives outside the domain layer.
 *
 * Flutter parity: mirrors `_getLocalCurrencyAmount` in
 * `lib/presentation/screens/main/main_screen.dart:207-220`, which reads
 * `WalletNotifier.wallet.currency`, calls `getRevolutExchangeRate(to:
 * currency)`, and sets `PaymentUtils.exchangeRate = exchangeRate.rate`
 * when non-null. The Flutter method name is misleading — it sets a rate,
 * not an amount — so this port is renamed to "refresh exchange rate".
 *
 * Both the class and a default instance are exported so the presentation
 * layer can use the singleton while tests can inject a stub service.
 */

import { PaymentService } from '@/domain/services/payment/paymentService';
import type { RevolutEnvironment } from '@/domain/enums/payment/revolutEnvironment';

export class RefreshLocalCurrencyExchangeRateUseCase {
  constructor(private service: PaymentService = new PaymentService()) { }

  async execute(
    toCurrency: string,
    environment: RevolutEnvironment,
    fromCurrency: string,
  ): Promise<number | null> {
    const entity = await this.service.getRevolutExchangeRate(toCurrency, environment, fromCurrency);
    return entity?.rate ?? null;
  }
}

export const refreshLocalCurrencyExchangeRateUseCase = new RefreshLocalCurrencyExchangeRateUseCase();
